import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Download, ArrowUpRight, Lock, CheckCircle2, Copy,
  FileText, Code, Target, TrendingUp, Heart, Calculator, Megaphone,
  ClipboardList, MessageSquare, Sparkles, FileDown,
  Palette, BarChart3, Users, GraduationCap, Settings, Scale,
  HardHat, Stethoscope, Pill, Building2, Bug, Landmark, Plane,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ROLE_LIBRARY } from "@/lib/roleLibrary";
import LanguagePicker from "@/components/LanguagePicker";
import Footer from "@/components/Footer";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Sample content per (region, lang) — drives the richer preview mockups.
const SAMPLE_CONTENT = {
  africa: {
    en: {
      name: "Amani Okonkwo", role: "Senior Backend Engineer",
      email: "amani@example.com", phone: "+254 712 345 678", location: "Nairobi, KE",
      summaryLabel: "Professional Summary",
      summary: "Senior backend engineer with 8 years scaling fintech rails. Led M-Pesa re-architecture; cut p99 latency 62%.",
      expLabel: "Experience", eduLabel: "Education", skillsLabel: "Skills",
      contactLabel: "Contact", certLabel: "Certifications",
      exp: [
        { role: "Staff Engineer", co: "Safaricom PLC", date: "Mar 2022 — Present", loc: "Nairobi",
          bullets: ["Led M-Pesa core re-architecture; cut p99 latency 62%", "Owned AWS spend KES 320M/yr; reduced 24%"] },
        { role: "Sr. Backend Engineer", co: "Cellulant", date: "Jan 2020 — Feb 2022", loc: "Lagos",
          bullets: ["Designed payments rail handling KES 14B annual TPV across 7 markets"] },
      ],
      edu: { school: "University of Nairobi", degree: "B.Sc Computer Science", date: "2014" },
      skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"],
      certs: ["AWS Solutions Architect Pro", "CKA"],
    },
    es: { name: "Amani Okonkwo", role: "Ingeniero Backend Senior", email: "amani@example.com", phone: "+254 712 345 678", location: "Nairobi, KE", summaryLabel: "Resumen Profesional", summary: "Ingeniero backend senior con 8 años escalando sistemas fintech. Lideré la re-arquitectura de M-Pesa; reduje latencia p99 en 62%.", expLabel: "Experiencia", eduLabel: "Educación", skillsLabel: "Habilidades", contactLabel: "Contacto", certLabel: "Certificaciones", exp: [{ role: "Ingeniero Principal", co: "Safaricom PLC", date: "Mar 2022 — Presente", loc: "Nairobi", bullets: ["Lideré re-arquitectura del núcleo M-Pesa; reduje latencia p99 62%", "Gestioné gasto AWS de KES 320M/año; reducción del 24%"] }, { role: "Ingeniero Backend Sr.", co: "Cellulant", date: "Ene 2020 — Feb 2022", loc: "Lagos", bullets: ["Diseñé infraestructura de pagos para 7 mercados, KES 14B TPV anual"] }], edu: { school: "Universidad de Nairobi", degree: "Lic. Ciencias de la Computación", date: "2014" }, skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"], certs: ["AWS Solutions Architect Pro", "CKA"] },
    de: { name: "Amani Okonkwo", role: "Senior Backend Engineer", email: "amani@example.com", phone: "+254 712 345 678", location: "Nairobi, KE", summaryLabel: "Berufliches Profil", summary: "Senior Backend Engineer mit 8 Jahren Erfahrung im Fintech. Leitete M-Pesa-Umstrukturierung; reduzierte p99-Latenz um 62%.", expLabel: "Berufserfahrung", eduLabel: "Ausbildung", skillsLabel: "Kenntnisse", contactLabel: "Kontakt", certLabel: "Zertifizierungen", exp: [{ role: "Staff Engineer", co: "Safaricom PLC", date: "Mär 2022 — Heute", loc: "Nairobi", bullets: ["Leitete M-Pesa-Kernarchitektur; senkte p99-Latenz um 62%", "Verwaltete AWS-Budget KES 320M/Jahr; reduziert um 24%"] }, { role: "Sr. Backend Engineer", co: "Cellulant", date: "Jan 2020 — Feb 2022", loc: "Lagos", bullets: ["Entwarf Zahlungsinfrastruktur für 7 Märkte mit KES 14B TPV/Jahr"] }], edu: { school: "Universität Nairobi", degree: "B.Sc. Informatik", date: "2014" }, skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"], certs: ["AWS Solutions Architect Pro", "CKA"] },
    fr: { name: "Amani Okonkwo", role: "Ingénieur Backend Senior", email: "amani@example.com", phone: "+254 712 345 678", location: "Nairobi, KE", summaryLabel: "Résumé Professionnel", summary: "Ingénieur backend senior avec 8 ans d'expérience en fintech. A dirigé la refonte de M-Pesa; latence p99 réduite de 62%.", expLabel: "Expérience Professionnelle", eduLabel: "Formation", skillsLabel: "Compétences", contactLabel: "Contact", certLabel: "Certifications", exp: [{ role: "Ingénieur Principal", co: "Safaricom PLC", date: "Mar 2022 — Présent", loc: "Nairobi", bullets: ["A dirigé la refonte du noyau M-Pesa; latence p99 réduite de 62%", "Géré le budget AWS de KES 320M/an; réduit de 24%"] }, { role: "Ingénieur Backend Sr.", co: "Cellulant", date: "Jan 2020 — Fév 2022", loc: "Lagos", bullets: ["Conçu l'infrastructure paiements pour 7 marchés, KES 14B TPV/an"] }], edu: { school: "Université de Nairobi", degree: "Lic. Informatique", date: "2014" }, skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"], certs: ["AWS Solutions Architect Pro", "CKA"] },
  },
  international: {
    en: {
      name: "Lukas Müller", role: "Senior Backend Engineer",
      email: "lukas@example.com", phone: "+49 30 1234 5678", location: "Berlin, DE",
      summaryLabel: "Professional Summary",
      summary: "Senior backend engineer with 8+ years building distributed systems at SAP and Stripe. Led migration cutting p99 latency 62%.",
      expLabel: "Experience", eduLabel: "Education", skillsLabel: "Skills",
      contactLabel: "Contact", certLabel: "Certifications",
      exp: [
        { role: "Staff Engineer", co: "SAP SE", date: "Mar 2022 — Present", loc: "Berlin",
          bullets: ["Led migration to Aurora-based core; cut p99 latency 62%", "Owned €2.1M cloud spend; reduced 24% via right-sizing"] },
        { role: "Sr. Backend Engineer", co: "Stripe", date: "Jan 2020 — Feb 2022", loc: "London",
          bullets: ["Designed payments rail handling €14B annual TPV across EMEA"] },
      ],
      edu: { school: "TU Munich", degree: "M.Sc Computer Science", date: "2014" },
      skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"],
      certs: ["AWS Solutions Architect Pro", "CKA"],
    },
    es: { name: "Sofía Martínez", role: "Ingeniera Backend Senior", email: "sofia@example.com", phone: "+34 612 345 678", location: "Madrid, ES", summaryLabel: "Resumen Profesional", summary: "Ingeniera backend senior con 8+ años construyendo sistemas distribuidos en BBVA y Stripe. Lideré migración reduciendo latencia p99 62%.", expLabel: "Experiencia", eduLabel: "Educación", skillsLabel: "Habilidades", contactLabel: "Contacto", certLabel: "Certificaciones", exp: [{ role: "Ingeniera Principal", co: "BBVA Open", date: "Mar 2022 — Presente", loc: "Madrid", bullets: ["Lideré migración a Aurora; reduje latencia p99 62%", "Gestioné gasto cloud de €2.1M; reducción del 24%"] }, { role: "Ingeniera Backend Sr.", co: "Stripe", date: "Ene 2020 — Feb 2022", loc: "Londres", bullets: ["Diseñé infraestructura de pagos para EMEA, €14B TPV anual"] }], edu: { school: "Universidad Politécnica de Madrid", degree: "M.Sc Informática", date: "2014" }, skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"], certs: ["AWS Solutions Architect Pro", "CKA"] },
    de: { name: "Lukas Müller", role: "Senior Backend Engineer", email: "lukas@example.com", phone: "+49 30 1234 5678", location: "Berlin, DE", summaryLabel: "Berufliches Profil", summary: "Senior Backend Engineer mit 8+ Jahren Erfahrung bei SAP und Stripe. Leitete Migration zur p99-Latenz-Reduzierung um 62%.", expLabel: "Berufserfahrung", eduLabel: "Ausbildung", skillsLabel: "Kenntnisse", contactLabel: "Kontakt", certLabel: "Zertifizierungen", exp: [{ role: "Staff Engineer", co: "SAP SE", date: "Mär 2022 — Heute", loc: "Berlin", bullets: ["Leitete Migration zum Aurora-Kern; senkte p99-Latenz um 62%", "Verwaltete Cloud-Budget €2,1M; reduziert um 24%"] }, { role: "Sr. Backend Engineer", co: "Stripe", date: "Jan 2020 — Feb 2022", loc: "London", bullets: ["Entwarf Zahlungsinfrastruktur für EMEA mit €14B TPV/Jahr"] }], edu: { school: "TU München", degree: "M.Sc. Informatik", date: "2014" }, skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"], certs: ["AWS Solutions Architect Pro", "CKA"] },
    fr: { name: "Camille Laurent", role: "Ingénieure Backend Senior", email: "camille@example.com", phone: "+33 6 12 34 56 78", location: "Paris, FR", summaryLabel: "Résumé Professionnel", summary: "Ingénieure backend senior avec 8+ années chez BNP Paribas et Stripe. A dirigé la migration réduisant la latence p99 de 62%.", expLabel: "Expérience Professionnelle", eduLabel: "Formation", skillsLabel: "Compétences", contactLabel: "Contact", certLabel: "Certifications", exp: [{ role: "Ingénieure Principale", co: "BNP Paribas", date: "Mar 2022 — Présent", loc: "Paris", bullets: ["A dirigé migration vers Aurora; latence p99 réduite de 62%", "Géré budget cloud de €2,1M; réduit de 24%"] }, { role: "Ingénieure Backend Sr.", co: "Stripe", date: "Jan 2020 — Fév 2022", loc: "Londres", bullets: ["Conçu infrastructure paiements EMEA, €14B TPV/an"] }], edu: { school: "École Polytechnique", degree: "M.Sc. Informatique", date: "2014" }, skills: ["Python", "Go", "AWS", "Kubernetes", "PostgreSQL", "gRPC"], certs: ["AWS Solutions Architect Pro", "CKA"] },
  },
};

function getSample(region, lang) {
  const bucket = SAMPLE_CONTENT[region] || SAMPLE_CONTENT.international;
  return bucket[lang] || bucket.en;
}

// Template visual descriptors — drives both the preview cards and the docx renderer keys
const TEMPLATES = [
  {
    key: "modern",
    name: "Aurora Modern",
    tagline: "Red accent, Calibri sans-serif, ATS-clean. The ResumeLab default.",
    bestFor: "Tech, fintech, growth-stage startups, global MNCs",
    palette: { bg: "#FFFFFF", accent: "#EF4444", heading: "#18181B", soft: "#FEF2F2" },
    fontFamily: "Calibri, system-ui, sans-serif",
    layout: "single",
    badge: "DEFAULT",
  },
  {
    key: "classic",
    name: "Heritage Classic",
    tagline: "Cambria serif, centered header, traditional borders. Recruiter-safe anywhere.",
    bestFor: "Banks, government, legal, traditional Mittelstand",
    palette: { bg: "#FFFFFF", accent: "#09090B", heading: "#09090B", soft: "#F5F5F4" },
    fontFamily: "Cambria, Georgia, serif",
    layout: "centered",
  },
  {
    key: "tech",
    name: "Vertex Tech",
    tagline: "Monochrome with green accent, mono headers. Strong engineering signal.",
    bestFor: "Backend, DevOps, infra engineers, OSS maintainers",
    palette: { bg: "#FFFFFF", accent: "#16A34A", heading: "#0F172A", soft: "#F0FDF4" },
    fontFamily: "'JetBrains Mono', Consolas, monospace",
    layout: "single",
  },
  {
    key: "executive",
    name: "Apex Executive",
    tagline: "Georgia serif, navy accent, refined whitespace. Director+ level.",
    bestFor: "VP / Director / C-level, MBA grads, board candidates",
    palette: { bg: "#FFFFFF", accent: "#1E40AF", heading: "#0F172A", soft: "#EFF6FF" },
    fontFamily: "Georgia, serif",
    layout: "centered",
  },
  {
    key: "two_column",
    name: "Atlas Two-Column",
    tagline: "Sidebar for contact + skills, main column for experience. Detail-dense.",
    bestFor: "Mid-career generalists, skills-heavy profiles",
    palette: { bg: "#FFFFFF", accent: "#EF4444", heading: "#18181B", soft: "#F4F4F5" },
    fontFamily: "Calibri, system-ui, sans-serif",
    layout: "two-col",
  },
  {
    key: "academic",
    name: "Scholar Academic",
    tagline: "Garamond serif, research-statement framing. PhD & research roles.",
    bestFor: "Faculty applications, PhD / postdoc, research scientists",
    palette: { bg: "#FFFFFF", accent: "#3F3F46", heading: "#18181B", soft: "#FAFAF9" },
    fontFamily: "Garamond, Georgia, serif",
    layout: "centered",
  },
  // ✨ NEW (#4 redesign)
  {
    key: "meridian",
    name: "Meridian Detail",
    tagline: "Two-column with avatar, achievements panel, dense quantified bullets.",
    bestFor: "Senior IC / staff engineers, designers with portfolio links",
    palette: { bg: "#FFFFFF", accent: "#0EA5E9", heading: "#0F172A", soft: "#F0F9FF" },
    fontFamily: "Calibri, system-ui, sans-serif",
    layout: "two-col",
    badge: "NEW",
    keyVariant: "meridian",
  },
  {
    key: "boulevard",
    name: "Boulevard Sidebar",
    tagline: "Centered header with horizontal section dividers. Polished, very readable.",
    bestFor: "MBA grads, management consultants, hospitality leadership",
    palette: { bg: "#FFFFFF", accent: "#7C3AED", heading: "#1E1B4B", soft: "#F5F3FF" },
    fontFamily: "Georgia, serif",
    layout: "centered",
    badge: "NEW",
    keyVariant: "boulevard",
  },
];

const ICONS = {
  Code, Target, TrendingUp, Heart, Calculator, Megaphone, ClipboardList, MessageSquare,
  Palette, BarChart3, Users, GraduationCap, Settings, Scale,
  HardHat, Stethoscope, Pill, Building2, Bug, Landmark, Plane,
};

function ResumeMockup({ tpl, sample }) {
  const { palette, fontFamily, layout, badge } = tpl;
  const initials = sample.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  // ============ TWO-COLUMN LAYOUT (Atlas, Meridian) ============
  if (layout === "two-col") {
    return (
      <div className="aspect-[3/4] border-b border-zinc-200 relative overflow-hidden" style={{ background: palette.bg, fontFamily }}>
        {badge ? (
          <div className="absolute top-3 right-3 px-2 py-0.5 font-mono text-[9px] tracking-widest z-10" style={{ backgroundColor: palette.accent, color: "#fff" }}>{badge}</div>
        ) : null}
        <div className="grid grid-cols-[38%_62%] h-full text-[8px] leading-tight">
          <div className="p-4 space-y-3" style={{ background: palette.soft }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-xs mb-2" style={{ background: palette.accent }}>{initials}</div>
            <div>
              <div className="font-bold text-[11px] leading-tight" style={{ color: palette.heading }}>{sample.name}</div>
              <div className="text-[8px]" style={{ color: palette.accent }}>{sample.role}</div>
            </div>
            <div className="space-y-1">
              <div className="font-bold uppercase tracking-wider text-[7px]" style={{ color: palette.heading }}>{sample.contactLabel}</div>
              <div className="text-zinc-600 break-all">{sample.email}</div>
              <div className="text-zinc-600">{sample.phone}</div>
              <div className="text-zinc-600">{sample.location}</div>
            </div>
            <div className="space-y-1">
              <div className="font-bold uppercase tracking-wider text-[7px]" style={{ color: palette.heading }}>{sample.skillsLabel}</div>
              <div className="flex flex-wrap gap-0.5">
                {sample.skills.slice(0, 6).map((s) => (
                  <span key={s} className="inline-block px-1 py-0.5 text-[6.5px]" style={{ background: "#fff", color: palette.body, border: `1px solid ${palette.accent}33` }}>{s}</span>
                ))}
              </div>
            </div>
            <div className="space-y-0.5">
              <div className="font-bold uppercase tracking-wider text-[7px]" style={{ color: palette.heading }}>{sample.certLabel}</div>
              {sample.certs.map((c) => <div key={c} className="text-zinc-600 text-[7px]">• {c}</div>)}
            </div>
          </div>
          <div className="p-4 space-y-2">
            <div>
              <div className="font-bold uppercase tracking-wider text-[8px] border-b pb-0.5" style={{ color: palette.heading, borderColor: palette.accent }}>{sample.summaryLabel}</div>
              <p className="text-zinc-700 text-[7.5px] leading-snug mt-1">{sample.summary}</p>
            </div>
            <div>
              <div className="font-bold uppercase tracking-wider text-[8px] border-b pb-0.5" style={{ color: palette.heading, borderColor: palette.accent }}>{sample.expLabel}</div>
              {sample.exp.map((e, i) => (
                <div key={i} className="mt-1.5">
                  <div className="flex justify-between items-baseline">
                    <span className="font-semibold text-[8px]" style={{ color: palette.heading }}>{e.role}</span>
                    <span className="text-[6.5px] text-zinc-500 italic">{e.date}</span>
                  </div>
                  <div className="text-[7.5px] font-semibold" style={{ color: palette.accent }}>{e.co} <span className="text-zinc-400 font-normal">· {e.loc}</span></div>
                  {e.bullets.map((b, j) => (
                    <div key={j} className="text-zinc-600 text-[7px] leading-snug mt-0.5">• {b}</div>
                  ))}
                </div>
              ))}
            </div>
            <div>
              <div className="font-bold uppercase tracking-wider text-[8px] border-b pb-0.5" style={{ color: palette.heading, borderColor: palette.accent }}>{sample.eduLabel}</div>
              <div className="flex justify-between items-baseline mt-1">
                <span className="font-semibold text-[8px]" style={{ color: palette.heading }}>{sample.edu.degree}</span>
                <span className="text-[6.5px] text-zinc-500 italic">{sample.edu.date}</span>
              </div>
              <div className="text-[7.5px]" style={{ color: palette.accent }}>{sample.edu.school}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ CENTERED LAYOUT (Classic, Executive, Academic, Boulevard) ============
  if (layout === "centered") {
    return (
      <div className="aspect-[3/4] border-b border-zinc-200 p-5 lg:p-6 relative overflow-hidden flex flex-col gap-1.5" style={{ background: palette.bg, fontFamily }}>
        {badge ? (
          <div className="absolute top-3 right-3 px-2 py-0.5 font-mono text-[9px] tracking-widest" style={{ backgroundColor: palette.accent, color: "#fff" }}>{badge}</div>
        ) : null}
        <div className="text-center">
          <div className="font-bold text-lg leading-tight" style={{ color: palette.heading }}>{sample.name}</div>
          <div className="text-[10px] font-semibold mt-0.5" style={{ color: palette.accent }}>{sample.role}</div>
          <div className="text-[7.5px] mt-1 text-zinc-500">{sample.email} · {sample.phone} · {sample.location}</div>
        </div>
        <div className="border-t pt-1 mt-1" style={{ borderColor: palette.accent + "44" }}>
          <div className="text-center font-bold uppercase tracking-widest text-[8px]" style={{ color: palette.heading }}>{sample.summaryLabel}</div>
          <p className="text-zinc-700 text-[7.5px] leading-snug mt-1 text-center px-2">{sample.summary}</p>
        </div>
        <div className="border-t pt-1 mt-1" style={{ borderColor: palette.accent + "44" }}>
          <div className="text-center font-bold uppercase tracking-widest text-[8px]" style={{ color: palette.heading }}>{sample.expLabel}</div>
          {sample.exp.map((e, i) => (
            <div key={i} className="mt-1.5">
              <div className="flex justify-between items-baseline">
                <span className="font-semibold text-[8px]" style={{ color: palette.heading }}>{e.role} — <span style={{ color: palette.accent }}>{e.co}</span></span>
                <span className="text-[6.5px] text-zinc-500 italic">{e.date}</span>
              </div>
              {e.bullets.slice(0, 2).map((b, j) => (
                <div key={j} className="text-zinc-600 text-[7px] leading-snug mt-0.5">• {b}</div>
              ))}
            </div>
          ))}
        </div>
        <div className="border-t pt-1 mt-1" style={{ borderColor: palette.accent + "44" }}>
          <div className="text-center font-bold uppercase tracking-widest text-[8px]" style={{ color: palette.heading }}>{sample.skillsLabel}</div>
          <div className="text-center text-[7.5px] text-zinc-600 mt-0.5">{sample.skills.join(" · ")}</div>
        </div>
      </div>
    );
  }

  // ============ SINGLE COLUMN (Modern, Tech) ============
  const isTech = tpl.key === "tech";
  return (
    <div className="aspect-[3/4] border-b border-zinc-200 p-5 lg:p-6 relative overflow-hidden flex flex-col gap-1.5" style={{ background: palette.bg, fontFamily }}>
      {badge ? (
        <div className="absolute top-3 right-3 px-2 py-0.5 font-mono text-[9px] tracking-widest" style={{ backgroundColor: palette.accent, color: "#fff" }}>{badge}</div>
      ) : null}
      <div>
        <div className="font-bold text-lg leading-tight" style={{ color: palette.heading }}>
          {isTech ? `> ${sample.name.toLowerCase().split(" ")[0]}` : sample.name}
        </div>
        <div className="text-[10px] font-semibold mt-0.5" style={{ color: palette.accent }}>{sample.role}</div>
        <div className="text-[7.5px] mt-1 text-zinc-500">{sample.email} {isTech ? "//" : "·"} {sample.phone} {isTech ? "//" : "·"} {sample.location}</div>
      </div>
      <div className="mt-1">
        <div className="font-bold uppercase tracking-wider text-[8px] border-b pb-0.5" style={{ color: palette.heading, borderColor: palette.accent }}>{sample.summaryLabel}</div>
        <p className="text-zinc-700 text-[7.5px] leading-snug mt-1">{sample.summary}</p>
      </div>
      <div>
        <div className="font-bold uppercase tracking-wider text-[8px] border-b pb-0.5" style={{ color: palette.heading, borderColor: palette.accent }}>{sample.expLabel}</div>
        {sample.exp.map((e, i) => (
          <div key={i} className="mt-1.5">
            <div className="flex justify-between items-baseline">
              <span className="font-semibold text-[8px]" style={{ color: palette.heading }}>{e.role}</span>
              <span className="text-[6.5px] text-zinc-500 italic">{e.date}</span>
            </div>
            <div className="text-[7.5px] font-semibold" style={{ color: palette.accent }}>{e.co} <span className="text-zinc-400 font-normal">· {e.loc}</span></div>
            {e.bullets.slice(0, 2).map((b, j) => (
              <div key={j} className="text-zinc-600 text-[7px] leading-snug mt-0.5">• {b}</div>
            ))}
          </div>
        ))}
      </div>
      <div>
        <div className="font-bold uppercase tracking-wider text-[8px] border-b pb-0.5" style={{ color: palette.heading, borderColor: palette.accent }}>{sample.skillsLabel}</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {sample.skills.slice(0, 6).map((s) => (
            <span key={s} className="inline-block px-1.5 py-0.5 text-[7px]" style={{ background: palette.soft, color: palette.body }}>{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TemplatePreviewCard({ tpl, region, lang }) {
  const [downloading, setDownloading] = useState(false);
  const sample = getSample(region, lang);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = `${API_BASE}/api/templates/preview/${tpl.key}?region=${region}&lang=${lang}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = `ResumeLab_Sample_${tpl.name.replace(/\s+/g, "_")}_${region === "international" ? "Intl" : "Africa"}_${(lang || "en").toUpperCase()}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(a.href);
      toast.success(`${tpl.name} sample (${region === "international" ? "International" : "African"}, ${(lang || "en").toUpperCase()}) downloaded`);
    } catch (e) {
      toast.error("Download failed", { description: e.message });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <article
      className="group bg-white border border-zinc-200 hover:border-zinc-400 transition-colors flex flex-col"
      data-testid={`template-card-${tpl.key}`}
    >
      <ResumeMockup tpl={tpl} sample={sample} />
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-bold text-xl text-zinc-900 mb-1">{tpl.name}</h3>
        <p className="text-sm text-zinc-600 leading-snug mb-2">{tpl.tagline}</p>
        <p className="text-xs text-zinc-500 mb-4">
          <span className="font-semibold text-zinc-700">Best for:</span> {tpl.bestFor}
        </p>
        <div className="mt-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            data-testid={`template-download-sample-${tpl.key}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors px-3 py-2 text-xs font-semibold disabled:opacity-60"
          >
            <FileDown className="w-3.5 h-3.5" />
            {downloading ? "Downloading…" : "Download sample"}
          </button>
          <Link
            to={`/builder?template=${tpl.key}`}
            data-testid={`template-use-${tpl.key}`}
            className="inline-flex items-center gap-1.5 bg-[#EF4444] hover:bg-[#DC2626] text-white px-3 py-2 text-xs font-semibold transition-colors"
          >
            Use it <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}

function RoleBulletCard({ role }) {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[role.icon] || FileText;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Bullet copied");
  };
  return (
    <article
      className="bg-white border border-zinc-200 hover:border-zinc-400 transition-colors"
      data-testid={`role-card-${role.slug}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-5 flex items-start gap-4 text-left"
        data-testid={`role-toggle-${role.slug}`}
      >
        <div className="w-10 h-10 bg-zinc-900 text-white grid place-items-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg text-zinc-900">{role.name}</h3>
          <p className="text-sm text-zinc-600 mt-1">{role.blurb}</p>
        </div>
        <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest pt-1">
          {open ? "Close" : `${role.bullets.length} bullets`}
        </div>
      </button>

      {/* SEO permalink — only visible-on-hover so it doesn't clutter the card */}
      <Link
        to={`/templates/role/${role.slug}`}
        className="block px-5 pb-3 -mt-1 text-xs text-zinc-400 hover:text-zinc-900 font-mono uppercase tracking-widest"
        data-testid={`role-permalink-${role.slug}`}
      >
        Open full page →
      </Link>

      {open ? (
        <div className="border-t border-zinc-200 p-5 lg:p-6 space-y-6" data-testid={`role-content-${role.slug}`}>
          {/* Summary starters */}
          <div>
            <div className="overline mb-2">◷ Summary starter</div>
            {role.summary_starters.map((s, i) => (
              <div key={i} className="mb-3 last:mb-0 group/copy flex items-start gap-3 bg-zinc-50 border border-zinc-200 p-4 text-sm text-zinc-700 leading-relaxed">
                <span className="flex-1">{s}</span>
                <button
                  type="button"
                  onClick={() => copy(s)}
                  className="text-zinc-400 hover:text-zinc-900 transition-colors opacity-0 group-hover/copy:opacity-100"
                  aria-label="Copy summary"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Bullets */}
          <div>
            <div className="overline mb-2">◆ Bullet library ({role.bullets.length})</div>
            <ul className="space-y-2">
              {role.bullets.map((b, i) => (
                <li key={i} className="group/copy flex items-start gap-3 bg-zinc-50 border-l-2 border-zinc-300 hover:border-[#EF4444] transition-colors px-4 py-3 text-sm text-zinc-700">
                  <span className="flex-1">{b}</span>
                  <button
                    type="button"
                    onClick={() => copy(b)}
                    data-testid={`role-bullet-copy-${role.slug}-${i}`}
                    className="text-zinc-400 hover:text-zinc-900 transition-colors opacity-0 group-hover/copy:opacity-100"
                    aria-label="Copy bullet"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Skills */}
          <div>
            <div className="overline mb-2">◇ Skills checklist</div>
            <div className="flex flex-wrap gap-2">
              {role.skills.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 bg-zinc-900 text-white px-2.5 py-1 text-xs font-medium"
                >
                  <CheckCircle2 className="w-3 h-3" /> {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}

export default function Templates() {
  const [region, setRegion] = useState("africa");
  const [lang, setLang] = useState(() => localStorage.getItem("rl_lang") || "en");
  const seoJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "name": "Free Resume Templates 2026 — Word + ATS-Friendly",
        "description": "Six recruiter-tested resume templates for African and global professionals. Download free samples in Word format, then audit your resume free to download the AI-revised version in your chosen style.",
        "url": "https://resumelab.co.ke/templates",
        "isPartOf": { "@type": "WebSite", "url": "https://resumelab.co.ke", "name": "ResumeLab" },
        "hasPart": TEMPLATES.map((t) => ({
          "@type": "CreativeWork",
          "name": `${t.name} Resume Template`,
          "description": t.tagline,
          "url": `https://resumelab.co.ke/templates#${t.key}`,
        })),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://resumelab.co.ke/" },
          { "@type": "ListItem", "position": 2, "name": "Templates", "item": "https://resumelab.co.ke/templates" },
        ],
      },
    ],
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid="templates-page">
      <SEO
        title="Free Resume Templates 2026 — Word Download · ATS-Friendly | ResumeLab"
        description="Six recruiter-tested resume templates for African and global professionals. Download free samples in Word format. ATS-friendly, role-specific bullet library, and instant AI revision."
        url="https://resumelab.co.ke/templates"
        jsonLd={seoJsonLd}
      />

      {/* Hero */}
      <section className="border-b border-zinc-200" data-testid="templates-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="overline mb-4">◷ FREE RESUME TEMPLATES · 2026</div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-zinc-900">
                Six templates.<br />
                <span className="text-[#EF4444]">One recruiter-approved scan.</span>
              </h1>
              <p className="mt-6 text-zinc-600 text-base lg:text-lg leading-relaxed max-w-2xl">
                ATS-clean Word templates built from real recruiter feedback across Nairobi, Lagos, Cape Town, Dubai, London, and the Bay Area. Download a sample in any style — then run your own resume through ResumeLab and download the AI-revised version in your chosen layout.
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="templates-hero-cta">
                <a
                  href="#templates-gallery"
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white px-6 py-3 text-sm font-semibold transition-colors"
                >
                  Browse templates <ArrowUpRight className="w-4 h-4" />
                </a>
                <a
                  href="#role-library"
                  className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-900 px-6 py-3 text-sm font-semibold transition-colors"
                >
                  Bullet library by role
                </a>
              </div>
            </div>

            <aside className="lg:col-span-4 bg-zinc-900 text-white p-8 flex flex-col justify-between">
              <div>
                <Sparkles className="w-6 h-6 text-[#EF4444] mb-3" />
                <h2 className="font-display font-bold text-2xl leading-tight">Get yours revised</h2>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                  Templates are great. <strong className="text-white">Templates with your content auto-rewritten by GPT-5.2</strong> are better. Free audit, pick your style, download.
                </p>
              </div>
              <Link
                to="/evaluate"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white px-5 py-3 text-sm font-semibold transition-colors"
                data-testid="templates-hero-audit-cta"
              >
                Audit my resume free <ArrowUpRight className="w-4 h-4" />
              </Link>
            </aside>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="templates-gallery" className="border-b border-zinc-200 bg-zinc-50" data-testid="templates-gallery">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="mb-8 lg:mb-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="overline mb-4">◆ VISUAL STYLES</div>
              <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight text-zinc-900">
                Six recruiter-tested layouts
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-3">
              <p className="text-zinc-600 text-base leading-relaxed mb-5">
                Each template is hand-tuned for ATS parsing (Workday, Greenhouse, Lever, Ashby, SmartRecruiters). Download the sample to see the typography, color, and section layout before deciding.
              </p>
              <div className="inline-flex items-center border border-zinc-300 bg-white" role="tablist" aria-label="Sample region" data-testid="region-toggle">
                <button
                  type="button"
                  onClick={() => setRegion("africa")}
                  data-testid="region-toggle-africa"
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${region === "africa" ? "bg-gradient-to-r from-orange-500 to-red-500 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                >
                  African sample
                </button>
                <button
                  type="button"
                  onClick={() => setRegion("international")}
                  data-testid="region-toggle-international"
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${region === "international" ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                >
                  International sample
                </button>
              </div>
              <div className="mt-5">
                <LanguagePicker
                  value={lang}
                  onChange={(c) => { setLang(c); localStorage.setItem("rl_lang", c); }}
                  label="Sample language"
                  testIdPrefix="templates-lang"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="templates-grid">
            {TEMPLATES.map((tpl) => (
              <TemplatePreviewCard key={tpl.key} tpl={tpl} region={region} lang={lang} />
            ))}
          </div>
        </div>
      </section>

      {/* Role bullet library */}
      <section id="role-library" className="border-b border-zinc-200 bg-white" data-testid="role-library-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="mb-8 lg:mb-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="overline mb-4">◷ ROLE-SPECIFIC BULLET LIBRARY</div>
              <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight text-zinc-900">
                Twenty-one roles.<br />
                Quantified, recruiter-tested.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-3">
              <p className="text-zinc-600 text-base leading-relaxed">
                Copy-pasteable bullets, summary starters, and skills checklists for twenty-one of the highest-volume roles in African and global English-speaking job markets. Tap any card to expand.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="role-grid">
            {ROLE_LIBRARY.map((role) => (
              <RoleBulletCard key={role.slug} role={role} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12 text-center">
          <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight">
            Templates are a starting line.<br />
            <span className="text-[#EF4444]">Your audit is the finish.</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mt-6 leading-relaxed">
            Run your resume through ResumeLab&apos;s free audit. We score it, identify gaps, and — for Pro members — generate the full revised version in any of these six templates.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/evaluate"
              className="inline-flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white px-8 py-4 text-base font-semibold transition-colors"
              data-testid="templates-bottom-cta"
            >
              Audit my resume free <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 border border-zinc-700 hover:bg-zinc-800 text-white px-8 py-4 text-base font-semibold transition-colors"
            >
              <Lock className="w-4 h-4" /> Pro pricing
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
